import type { Community } from '@communities/community/domain';
import { FEATURE_FLAGS } from '@core/domain';
import type { ComprehensiveAnalytics } from '@core/graphql/types/schema';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { CommunityAuditAccuracyChart } from '@internal-audit/components/CommunityAuditAccuracyChart';
import { CommunityAuditLabelAnalysis } from '@internal-audit/components/CommunityAuditLabelAnalysis';
import { CommunityAuditPerformanceMetrics } from '@internal-audit/components/CommunityAuditPerformanceMetrics';
import { CommunityAuditPolicyBreakdown } from '@internal-audit/components/CommunityAuditPolicyBreakdown';
import { CommunityAuditProductionStatus } from '@internal-audit/components/CommunityAuditProductionStatus';
import type { AuditReport } from '@internal-audit/domain';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { Dropdown, PageHeader } from '@soteria/design-system-ve';
import { useCallback, useMemo, useState } from 'react';

import * as styles from './CommunityAuditHeaderSection.css';

type AnalyticsViewMode = 'labels' | 'policies';

const VIEW_MODE_OPTIONS = [
  { id: 'labels', label: 'Labels', value: 'labels' },
  { id: 'policies', label: 'Policies', value: 'policies' },
];

interface CommunityAuditHeaderSectionProps {
  community: Community | null;
  auditSessions: FlexibleAuditSession[];
  auditReports?: AuditReport[];
  isLoading?: boolean;
}

/**
 * Audit Analytics Panels Grid - can be used in header (V1) or page body (V2)
 * Uses the unified ComprehensiveAnalytics interface from the hook that matches the GraphQL query.
 */
export interface AuditAnalyticsPanelsProps {
  auditSessions: FlexibleAuditSession[];
  auditReports: AuditReport[];
  comprehensiveAnalytics: ComprehensiveAnalytics | null;
  isLoading?: boolean;
}

export function AuditAnalyticsSection({ auditSessions, auditReports, comprehensiveAnalytics, isLoading = false }: AuditAnalyticsPanelsProps) {
  const isPolicyMetricsEnabled = useIsFeatureEnabled(FEATURE_FLAGS.AUDIT_POLICY_METRICS);
  const [viewMode, setViewMode] = useState<AnalyticsViewMode>('labels');

  const handleViewModeChange = useCallback((value: string) => {
    if (value === 'labels' || value === 'policies') {
      setViewMode(value);
    }
  }, []);

  // When feature flag is off, force labels view
  const activeViewMode = isPolicyMetricsEnabled ? viewMode : 'labels';

  // Transform reports for policy chart: replace overall metrics with overallPolicy classification metrics
  const policyChartReports = useMemo(() => {
    if (activeViewMode !== 'policies') return auditReports;

    return auditReports.map(report => ({
      ...report,
      metrics: report.metrics?.overallPolicy
        ? {
            ...report.metrics,
            overall: {
              precision: report.metrics.overallPolicy.precision,
              recall: report.metrics.overallPolicy.recall,
              f1Score: report.metrics.overallPolicy.f1Score,
              accuracy: report.metrics.overallPolicy.accuracy,
            },
          }
        : report.metrics,
    }));
  }, [activeViewMode, auditReports]);

  // Swap performance metrics source based on view mode
  const activePerformanceAnalytics = useMemo(() => {
    if (activeViewMode === 'labels' || !comprehensiveAnalytics) return comprehensiveAnalytics;

    return {
      ...comprehensiveAnalytics,
      performanceMetrics: comprehensiveAnalytics.policyPerformanceMetrics || {
        avgAccuracy: null,
        avgPrecision: null,
        avgRecall: null,
        avgF1Score: null,
      },
    };
  }, [activeViewMode, comprehensiveAnalytics]);

  return (
    <div className={styles.analyticsSection}>
      {/* 4-Panel Grid Layout */}
      <div className={styles.fourPanelContainer}>
        {/* Labels / Policies Toggle — gated by feature flag */}
        {isPolicyMetricsEnabled && (
          <div className={styles.toggleContainer}>
            <label className={styles.toggleLabel} htmlFor="analytics-view-mode">
              Metrics view:
            </label>
            <Dropdown
              id="analytics-view-mode"
              options={VIEW_MODE_OPTIONS}
              placeholder="View mode"
              selectedItem={activeViewMode}
              size="medium"
              onSelectionChange={handleViewModeChange}
            />
          </div>
        )}
        <div className={styles.fourPanelGrid}>
          {/* Performance Metrics Over Time Chart Panel — same component, different data */}
          <CommunityAuditAccuracyChart
            auditReports={activeViewMode === 'labels' ? auditReports : policyChartReports}
            auditSessions={auditSessions}
            isLoading={isLoading}
          />

          {/* Performance Metrics Bar Chart Panel — same component, different data */}
          <CommunityAuditPerformanceMetrics comprehensiveAnalytics={activePerformanceAnalytics} isLoading={isLoading} />

          {/* Breakdown Panel — different component per view */}
          {activeViewMode === 'labels' ? (
            <CommunityAuditLabelAnalysis comprehensiveAnalytics={comprehensiveAnalytics} isLoading={isLoading} />
          ) : (
            <CommunityAuditPolicyBreakdown auditReports={auditReports} comprehensiveAnalytics={comprehensiveAnalytics} isLoading={isLoading} />
          )}

          {/* Audit History Panel — Always visible */}
          <CommunityAuditProductionStatus auditSessions={auditSessions} comprehensiveAnalytics={comprehensiveAnalytics} isLoading={isLoading} />

          {/* Consolidated Footer for Analytics Panels */}
          <div className={styles.analyticsFooter}>
            <span className={styles.analyticsFooterText}>Averaged across all completed audit reports (max 100)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Header section component for audit pages.
 * Renders a PageHeader with the community audit title.
 * Analytics panels are rendered separately in the page body via AuditAnalyticsSection.
 */
function CommunityAuditHeaderSectionComponent({ community }: CommunityAuditHeaderSectionProps) {
  const name = community?.name || '';

  return <PageHeader data-testid="community-audit-header-v2" title={`${name} Audits`} />;
}

export const CommunityAuditHeaderSection = withAppInsightsTracking(CommunityAuditHeaderSectionComponent, 'CommunityAuditHeaderSection');
